import React, { createContext, useContext, useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut, signIn, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import awsconfig from '../aws-exports';
import { User, UserRole } from '../types';

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: awsconfig.aws_user_pools_id,
      userPoolClientId: awsconfig.aws_user_pools_web_client_id,
      signUpVerificationMethod: 'code'
    }
  },
  API: {
    REST: {
      PsychicLampAPI: {
        endpoint: 'https://17igbr2mm5.execute-api.eu-west-1.amazonaws.com/dev',
        region: 'eu-west-1'
      }
    }
  }
});

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getAuthToken: () => Promise<string | null>;
  isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      // Development mode bypass - create a mock admin user
      if (import.meta.env.DEV || import.meta.env.MODE === 'development') {
        setUser({
          id: 'dev-admin',
          username: 'admin',
          email: 'admin@psychiclamp.local',
          role: UserRole.ADMIN,
        });
        setLoading(false);
        return;
      }

      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      
      setUser({
        id: currentUser.userId,
        username: currentUser.username,
        email: userAttributes.email || '',
        role: (userAttributes['custom:role'] as UserRole) || UserRole.VIEWER,
      });
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (username: string, password: string) => {
    try {
      setLoading(true);
      
      // Development mode bypass
      if (import.meta.env.DEV || import.meta.env.MODE === 'development') {
        // Mock successful sign in for development
        setUser({
          id: 'dev-admin',
          username: 'admin',
          email: 'admin@psychiclamp.local',
          role: UserRole.ADMIN,
        });
        setLoading(false);
        return { isSignedIn: true };
      }

      const result = await signIn({ username, password });
      await checkUser();
      return result;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };



  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getAuthToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  };

  const isAdmin = (): boolean => {
    return user?.role === UserRole.ADMIN;
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn: handleSignIn,
    signOut: handleSignOut,
    getAuthToken,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};